import { BitkitLink, BitkitPromoBanner, BitkitPromoBannerProps } from '@bitrise/bitkit-v2';

import { useDismissableNotification } from '@/hooks/useDismissableNotification';

const DOWNLOAD_FOR_MAC_URL = 'https://github.com/bitrise-io/bitrise-desktop-app/releases/latest/download/Bitrise.dmg';
const LEARN_MORE_URL = 'https://docs.bitrise.io/en/bitrise-ci/run-and-analyze-builds/bitrise-desktop-app-for-macos';

// Exported for Storybook decorators that reset/set the dismissal cookie.
export const macAppPromoBannerCookieName = (dismissKey: string) => `mac_app_promo_banner_dismissed_${dismissKey}`;

// Visibility lives in the hook (not the component) so pages can hide sibling layout elements (e.g. a Separator)
// together with the banner.
export const useMacAppPromoBanner = (dismissKey: string) => {
  const { isHidden, dismiss } = useDismissableNotification(macAppPromoBannerCookieName(dismissKey));

  return { isVisible: !isHidden, onDismiss: dismiss };
};

type MacAppPromoBannerProps = Omit<BitkitPromoBannerProps, 'actionable' | 'dismissible' | 'onClose'> & {
  onDismiss: () => void;
  onDownloadClick?: () => void;
};

const MacAppPromoBanner = ({ onDismiss, onDownloadClick, message, ...rest }: MacAppPromoBannerProps) => {
  return (
    <BitkitPromoBanner
      message={
        <>
          {message}{' '}
          <BitkitLink href={LEARN_MORE_URL} textDecoration="underline" isExternal>
            Learn more
          </BitkitLink>
        </>
      }
      actionable={{ label: 'Download for Mac', href: DOWNLOAD_FOR_MAC_URL, onClick: onDownloadClick }}
      dismissible
      onClose={onDismiss}
      {...rest}
    />
  );
};

export default MacAppPromoBanner;
